import PropTypes from "prop-types";
import { Tooltip } from "antd";
import styled from "styled-components";
import { Star, Warning } from "@phosphor-icons/react";
import tokens from "../../theme/tokens";

/**
 * Small pill rendered next to a document name in the matter document
 * table when the document already participates in the categorization
 * library. Two variants:
 *
 *   - `gold`     → "Gold standard" (positive example pinned by an
 *                  attorney or admin).
 *   - `reported` → "Reported" (someone flagged the AI's category as
 *                  wrong; the row's `actual_category` is now part of
 *                  the negative-example training data).
 *
 * If both flags are true on the same doc, the "Reported" variant wins
 * because it's the actionable / corrective signal — the gold standard
 * is implicit in the paired correction.
 */

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: ${tokens.radius.pill};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "reported" ? tokens.status.review.border : tokens.status.ready.border};
  background: ${({ $tone }) =>
    $tone === "reported" ? tokens.status.review.bg : tokens.status.ready.bg};
  color: ${({ $tone }) =>
    $tone === "reported" ? tokens.status.review.fg : tokens.status.ready.fg};
  font-family: ${tokens.typography.fontFamily};
  font-size: ${tokens.typography.size.xs};
  font-weight: ${tokens.typography.weight.semibold};
  line-height: 1.4;
  white-space: nowrap;
  vertical-align: baseline;
`;

const ICONS = {
  gold: Star,
  reported: Warning,
};

const LABELS = {
  gold: "Gold standard",
  reported: "Reported",
};

const TOOLTIPS = {
  gold: "This document is in the categorization gold standard library.",
  reported:
    "An attorney flagged the AI's category as wrong. The correction is now training data.",
};

export const pickCategorizationTone = ({ goldStandard, negativeExample }) => {
  if (negativeExample) return "reported";
  if (goldStandard) return "gold";
  return null;
};

const CategorizationBadge = ({ goldStandard, negativeExample, size, className }) => {
  const tone = pickCategorizationTone({ goldStandard, negativeExample });
  if (!tone) return null;

  const Icon = ICONS[tone];
  const label = LABELS[tone];
  const tooltip = TOOLTIPS[tone];
  const iconSize = size === "sm" ? 10 : 12;

  return (
    <Tooltip title={tooltip} placement="top">
      <Pill
        $tone={tone}
        className={className}
        data-testid={`categorization-badge-${tone}`}
        role="note"
        aria-label={`${label}: ${tooltip}`}
      >
        <Icon
          size={iconSize}
          weight="fill"
          aria-hidden="true"
        />
        {label}
      </Pill>
    </Tooltip>
  );
};

CategorizationBadge.propTypes = {
  goldStandard: PropTypes.bool,
  negativeExample: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md"]),
  className: PropTypes.string,
};

CategorizationBadge.defaultProps = {
  goldStandard: false,
  negativeExample: false,
  size: "sm",
  className: undefined,
};

export default CategorizationBadge;
